import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, PersonStanding } from 'lucide-react';
import { MuscleBodies, MuscleLegend } from '../workout/MuscleBodies';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role chip wording (map preview is Spanish-first, matching the app's client UI)
const ROLE_LABELS = {
  es: { primary: 'principal', synergist: 'secundario', stabilizer: 'estabilizador' },
  en: { primary: 'primary', synergist: 'synergist', stabilizer: 'stabilizer' },
};
const ROLE_STYLES = {
  primary: 'bg-red-500/20 text-red-200 border-red-400/30',
  synergist: 'bg-amber-500/20 text-amber-200 border-amber-400/30',
  stabilizer: 'bg-slate-500/20 text-slate-200 border-slate-400/30',
};

const LEGEND = {
  es: { none: 'Sin trabajo', levels: ['Leve', 'Moderado', 'Alto', 'Máximo'] },
  en: { none: 'None', levels: ['Light', 'Moderate', 'High', 'Max'] },
};
const BODY_LABELS = { es: { front: 'Frente', back: 'Espalda' }, en: { front: 'Front', back: 'Back' } };

/**
 * Shows which body regions an exercise lights up, computed by the SAME backend
 * scoring used after a workout (primary x1, synergist x0.5, stabilizer x0.25).
 * Uses a single synthetic completed set so the preview reflects relative emphasis.
 */
const MuscleMapPreviewModal = ({ exercise, language = 'es', onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const lang = language === 'en' ? 'en' : 'es';

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await axios.post(`${API}/workouts/muscle-summary`, {
          language: lang,
          exercises: [{
            exercise_id: exercise.id,
            exercise_name: exercise.name,
            actual_sets: [{ reps: 1, weight: 1, completed: true }],
          }],
        });
        if (active) setData(res.data);
      } catch (e) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [exercise.id, exercise.name, lang]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const regions = data?.regions || [];
  const isCardio = exercise.exercise_type === 'cardio' || exercise.category === 'cardio';

  // Portal to <body>: ancestor .glass-card has backdrop-filter/transform, which would
  // otherwise trap position:fixed inside the card instead of the viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="muscle-preview-modal"
    >
      <div
        className="bg-slate-900 border border-blue-400/20 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-slate-900 z-10">
          <div className="flex items-center gap-2 min-w-0">
            <PersonStanding className="w-5 h-5 text-blue-300 shrink-0" />
            <div className="min-w-0">
              <h3 className="text-white font-semibold truncate" data-testid="muscle-preview-title">
                {lang === 'es' ? 'Mapa muscular' : 'Muscle map'}
              </h3>
              <p className="text-blue-200/70 text-xs truncate">{exercise.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
            data-testid="muscle-preview-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {loading && (
            <div className="py-12 text-center text-blue-200/70 text-sm" data-testid="muscle-preview-loading">
              {lang === 'es' ? 'Calculando…' : 'Computing…'}
            </div>
          )}

          {!loading && error && (
            <div className="py-12 text-center text-red-300 text-sm" data-testid="muscle-preview-error">
              {lang === 'es' ? 'No se pudo generar el mapa.' : 'Could not generate the map.'}
            </div>
          )}

          {!loading && !error && (
            <>
              <MuscleBodies data={data} labels={BODY_LABELS[lang]} />
              <MuscleLegend t={LEGEND[lang]} />

              {isCardio && regions.length === 0 ? (
                <div className="mt-4 text-center text-slate-400 text-sm" data-testid="muscle-preview-cardio">
                  {lang === 'es'
                    ? 'Ejercicio de cardio — no se mapea a grupos musculares.'
                    : 'Cardio exercise — not mapped to muscle groups.'}
                </div>
              ) : regions.length === 0 ? (
                <div className="mt-4 text-center text-slate-400 text-sm" data-testid="muscle-preview-empty">
                  {lang === 'es'
                    ? 'Sin datos musculares para este ejercicio.'
                    : 'No muscle data for this exercise.'}
                </div>
              ) : (
                <div className="mt-5 space-y-1.5" data-testid="muscle-preview-list">
                  {regions.map((r) => {
                    const roles = Array.from(new Set((r.exercises || []).map((e) => e.role)));
                    return (
                      <div
                        key={r.slug}
                        className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg bg-white/[0.03]"
                        data-testid={`muscle-preview-row-${r.slug}`}
                      >
                        <span className="text-slate-100 text-sm">{r.label}</span>
                        <div className="flex items-center gap-1">
                          {roles.map((role) => (
                            <span
                              key={role}
                              className={`text-[10px] px-1.5 py-0.5 rounded border ${ROLE_STYLES[role] || ROLE_STYLES.stabilizer}`}
                            >
                              {ROLE_LABELS[lang][role] || role}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {data?.unmapped?.length > 0 && (
                <p className="mt-3 text-[11px] text-amber-300/80" data-testid="muscle-preview-unmapped">
                  {lang === 'es' ? 'No reconocidos: ' : 'Unrecognized: '}
                  {data.unmapped.join(', ')}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MuscleMapPreviewModal;
